import { useMemo, useState } from 'react';
import { Badge, Button, Container, Group, Paper, Stack, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTractsList } from '../../queries/tracts';
import { TractFormDrawer } from './TractFormDrawer';
import type { Tract } from '../../types/api';

export function TractsListPage() {
  const [stateFilter, setStateFilter] = useState('');
  const [countyFilter, setCountyFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filters = useMemo(
    () => ({
      state: stateFilter || undefined,
      county: countyFilter || undefined,
    }),
    [stateFilter, countyFilter],
  );

  const { data: tracts = [], isLoading, isError, error } = useTractsList(filters);
  const navigate = useNavigate();

  return (
    <Container size="xl">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Stack gap={0}>
            <Title order={2}>Tracts</Title>
            {tracts.length > 0 && (
              <Badge variant="light" color="pine" size="sm">
                {tracts.length} tract{tracts.length === 1 ? '' : 's'}
              </Badge>
            )}
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)}>
            New tract
          </Button>
        </Group>

        <Paper withBorder p="sm" radius="md">
          <Group gap="sm">
            <TextInput
              placeholder="State (e.g. TX)"
              leftSection={<IconSearch size={14} />}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.currentTarget.value)}
              w={160}
            />
            <TextInput
              placeholder="County"
              leftSection={<IconSearch size={14} />}
              value={countyFilter}
              onChange={(e) => setCountyFilter(e.currentTarget.value)}
              w={220}
            />
          </Group>
        </Paper>

        <Paper withBorder radius="md">
          <DataTable<Tract>
            records={tracts}
            fetching={isLoading}
            minHeight={200}
            noRecordsText={
              isError ? `Failed to load: ${(error as Error).message}` : 'No tracts yet'
            }
            highlightOnHover
            onRowClick={({ record }) => navigate(`/tracts/${record.id}`)}
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                render: (r) => (
                  <Link to={`/tracts/${r.id}`} style={{ textDecoration: 'none' }}>
                    {r.name}
                  </Link>
                ),
              },
              { accessor: 'state', title: 'State', width: 70 },
              { accessor: 'county', title: 'County', width: 160 },
              {
                accessor: 'gross_acres',
                title: 'Gross acres',
                textAlign: 'right',
                width: 120,
                render: (r) =>
                  r.gross_acres == null ? '—' : Number(r.gross_acres).toLocaleString(),
              },
              {
                accessor: 'updated_at',
                title: 'Updated',
                width: 160,
                render: (r) => dayjs(r.updated_at).format('YYYY-MM-DD HH:mm'),
              },
            ]}
          />
        </Paper>

        <TractFormDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} />
      </Stack>
    </Container>
  );
}
